import { useApi } from "@/lib/api/useApi";
import { useMutate } from "@/lib/api/useMutate";
import { queryCache } from "@/lib/api/queryCache";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { addToCart } from "@/lib/api/cart";
import { cartQuery, favoriteIdsQuery, favoritesQuery } from "@/lib/api/queries";
import type { Product } from "@/lib/api/types";
import { removeFavorite } from "@/lib/api/wishlist";
import { EmptyState } from "@/components/store/EmptyState";
import { Icon } from "@/components/store/Icon";
import { Reveal } from "@/components/motion/Reveal";
import { PageTransition } from "@/components/store/PageTransition";
import { StoreLayout } from "@/components/layout/StoreLayout";
import { useFormatters } from "@/lib/format";
import { useLocalizedContent } from "@/lib/localize";
import { imgUrl } from "@/lib/format";
import { ProductCard } from "@/components/store/ProductCard";
import { ProductGridSkeleton } from "@/components/store/ProductCardSkeleton";

function FavoritesPage() {
  const { t } = useTranslation();
  const { formatPrice } = useFormatters();
  const { productName, productCategory } = useLocalizedContent();
  const fq = favoritesQuery();
  const { data: items = [], loading } = useApi(fq.queryKey, fq.queryFn);

  const invalidate = () => {
    queryCache.invalidate(favoritesQuery().queryKey);
    queryCache.invalidate(favoriteIdsQuery().queryKey);
  };

  const removeMutation = useMutate(
    (id: string) => removeFavorite(id),
    {
      onSuccess: () => {
        invalidate();
        toast.success(t("favorites.removed"));
      },
      onError: (error: Error) => toast.error(error.message),
    }
  );

  const addMutation = useMutate(
    (product: Product) =>
      addToCart({
        productId: product.id,
        color: product.colors[0].name,
        size: product.sizes[0],
      }),
    {
      onSuccess: (_data, product) => {
        queryCache.invalidate(cartQuery().queryKey);
        toast.success(t("common.addedToBag", { name: productName(product.name) }));
      },
      onError: (error: Error) => toast.error(error.message),
    }
  );

  return (
    <StoreLayout>
      <PageTransition>
      <main className="pt-28 md:pt-10 pb-section-gap-desktop max-w-7xl mx-auto px-grid-margin">
        <Reveal as="header" className="mb-8 md:mb-12">
          <h1 className="text-headline-lg font-headline-lg text-primary">{t("favorites.title")}</h1>
        </Reveal>

        {loading ? (
          <ProductGridSkeleton count={4} />
        ) : items.length === 0 ? (
          <EmptyState
            variant="favorites"
            title={t("favorites.empty")}
            description={t("favorites.emptyDesc")}
          />
        ) : (
          <motion.div
            layout
            className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-gutter gap-y-10 items-stretch"
          >
            <AnimatePresence mode="popLayout">
            {items.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
            </AnimatePresence>
          </motion.div>
        )}

      </main>
      </PageTransition>
    </StoreLayout>
  );
}

export default FavoritesPage;
